// src/components/feed/AnnotatedImage.tsx
//
// Image renderer that overlays clickable dots on each annotated
// feature. Driven by usePhotoAnnotations, which subscribes to the
// background-generated /photo_annotations/{hash} doc.
//
// Behavior:
//   • Image renders immediately via useResolvedImageUrl.
//   • Dots fade in (opacity transition) once annotations arrive.
//   • Tap a dot → highlights it + opens a callout near the dot
//     with the label and explanation. Tap dot again or anywhere
//     else on the image to dismiss.
//   • If the annotation has `more_details`, the callout shows a
//     "More details" button that opens AnnotationDetailsModal.
//   • If annotations never arrive (URL not a storage path, function
//     hasn't run, function failed) the image renders normally with
//     no dots — silent degradation.
//
// Layout strategy: dots are absolute-positioned at the box anchor
// (center of bounding box), in normalized 0..1 percentages so the
// overlay scales with the rendered image. The callout, by
// contrast, is positioned in PIXELS computed from a measured
// container size — percentages alone aren't enough to keep the
// callout fully on-screen on narrow viewports, because the
// callout has a fixed minimum width that doesn't scale with the
// image. The renderer measures the container with ResizeObserver,
// then clamps the callout's left/right edges within the container
// box plus an edge padding. Vertical placement flips above/below
// the dot based on which side has more room.

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { Close, OpenInFull } from '@mui/icons-material';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';
import {
  usePhotoAnnotations,
  type PhotoAnnotation,
} from '../../feed/usePhotoAnnotations';
import { AnnotationDetailsModal } from './AnnotationDetailsModal';
import { useT } from '../../i18n/useT';

interface Props {
  /** Storage path or URL. Annotations only fetched for storage paths. */
  source: string | null | undefined;
  alt?: string;
  /** Optional max-height for the image. Ignored in lightbox mode. */
  maxHeight?: number;
  /** Image fit. Feed cards use 'cover'; chat bubbles use 'contain'.
   *  Lightbox mode forces 'contain' regardless. */
  objectFit?: 'cover' | 'contain';
  /** Optional border radius on the rendered image. */
  borderRadius?: number;
  /** Optional top margin. */
  marginTop?: number;
  /** Layout mode. 'inline' (default) full-width with maxHeight;
   *  'lightbox' container hugs image's intrinsic aspect. */
  mode?: 'inline' | 'lightbox';
  /** Feed item id, when this image is being rendered as part of
   *  a feed_item. Required for non-owner viewers to load the
   *  image — `useResolvedImageUrl` routes their request through
   *  the feedImageProxy Cloud Function, which checks the
   *  feed_item's visibility before signing a storage URL.
   *  Owners get the fast direct getDownloadURL path either way. */
  feedItemId?: string;
  /** When provided, image is clickable. Click is suppressed when
   *  the user is dismissing an active callout. */
  onImageClick?: () => void;
}

// Dot color by annotation category. Mirrors the chip palette in the
// chat / saved-message renderer so the visual language is consistent.
const CATEGORY_COLOR: Record<string, string> = {
  architecture: '#0284c7',
  art: '#db2777',
  place: '#059669',
  person: '#7c3aed',
  event: '#d97706',
  concept: '#db2777',
  period: '#7c3aed',
  nature: '#16a34a',
  object: '#4b5563',
};
const colorFor = (cat: string): string =>
  CATEGORY_COLOR[cat] || CATEGORY_COLOR.object;

// Callout sizing constants. MIN width is what we reserve when
// computing edge clamping, so even on narrow screens we guarantee
// enough room for label + close button + ~one line of body.
const CALLOUT_MIN_W = 200;
const CALLOUT_MAX_W = 280;
const CALLOUT_EDGE_PAD = 8;
const CALLOUT_DOT_GAP = 18;

export const AnnotatedImage: React.FC<Props> = ({
  source,
  alt,
  maxHeight = 360,
  objectFit = 'cover',
  borderRadius = 0,
  marginTop = 0,
  mode = 'inline',
  feedItemId,
  onImageClick,
}) => {
  const t = useT();
  const resolvedUrl = useResolvedImageUrl(source ?? null, feedItemId);
  const { annotations, status } = usePhotoAnnotations(source ?? null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calloutRef = useRef<HTMLDivElement | null>(null);

  const [containerSize, setContainerSize] = useState<{
    w: number;
    h: number;
  }>({ w: 0, h: 0 });

  const [calloutSize, setCalloutSize] = useState<{
    w: number;
    h: number;
  }>({ w: CALLOUT_MIN_W, h: 80 });

  const dotsVisible = status === 'complete' && annotations.length > 0;
  const activeAnnotation = useMemo<PhotoAnnotation | null>(() => {
    if (!activeId) return null;
    return annotations.find((a) => a.id === activeId) ?? null;
  }, [activeId, annotations]);

  // Track container size with ResizeObserver. Falls back to a
  // single measurement on layout-effect mount if the browser
  // doesn't have ResizeObserver.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ w: rect.width, h: rect.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure the callout after it renders so we know its actual
  // height for vertical clamping. Re-runs whenever the active
  // annotation changes.
  useLayoutEffect(() => {
    const el = calloutRef.current;
    if (!el || !activeAnnotation) return;
    const id = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      setCalloutSize({ w: rect.width, h: rect.height });
    });
    return () => cancelAnimationFrame(id);
  }, [activeAnnotation]);

  const handleDotClick = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      setActiveId((cur) => (cur === id ? null : id));
    },
    []
  );

  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (activeId) {
        setActiveId(null);
        return;
      }
      onImageClick?.();
    },
    [activeId, onImageClick]
  );

  const openDetails = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDetailsOpen(true);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  // Compute the callout's pixel position so it stays fully within
  // the container box. Returns null when we don't yet have a
  // container measurement (first paint).
  const calloutPosition = useMemo<{
    left: number;
    top: number;
  } | null>(() => {
    if (!activeAnnotation) return null;
    const { w: cw, h: ch } = containerSize;
    if (cw <= 0 || ch <= 0) return null;
    const dotX = activeAnnotation.anchor.x * cw;
    const dotY = activeAnnotation.anchor.y * ch;

    // Decide vertical side based on which has more room (in pixels,
    // not percentages).
    const roomBelow = ch - dotY - CALLOUT_DOT_GAP;
    const roomAbove = dotY - CALLOUT_DOT_GAP;
    const placeBelow =
      roomBelow >= calloutSize.h ||
      roomBelow >= roomAbove;

    const top = placeBelow
      ? Math.min(
          dotY + CALLOUT_DOT_GAP,
          Math.max(CALLOUT_EDGE_PAD, ch - calloutSize.h - CALLOUT_EDGE_PAD)
        )
      : Math.max(
          CALLOUT_EDGE_PAD,
          dotY - CALLOUT_DOT_GAP - calloutSize.h
        );

    const effectiveW = Math.max(calloutSize.w, CALLOUT_MIN_W);
    const idealLeft = dotX - effectiveW / 2;
    const minLeft = CALLOUT_EDGE_PAD;
    const maxLeft = cw - effectiveW - CALLOUT_EDGE_PAD;
    const left =
      maxLeft < minLeft
        ? minLeft
        : Math.min(Math.max(idealLeft, minLeft), maxLeft);

    return { left, top };
  }, [activeAnnotation, containerSize, calloutSize]);

  if (!resolvedUrl) return null;

  const isLightbox = mode === 'lightbox';
  const containerSx = isLightbox
    ? {
        position: 'relative' as const,
        display: 'inline-block',
        maxWidth: '100%',
        maxHeight: { xs: '100vh', sm: '90vh' },
        borderRadius: borderRadius ? `${borderRadius}px` : 0,
        marginTop: marginTop ? `${marginTop}px` : 0,
      }
    : {
        position: 'relative' as const,
        width: '100%',
        maxHeight,
        overflow: 'hidden' as const,
        borderRadius: borderRadius ? `${borderRadius}px` : 0,
        marginTop: marginTop ? `${marginTop}px` : 0,
      };
  const imgSx = isLightbox
    ? {
        maxWidth: '100%',
        maxHeight: { xs: '100vh', sm: '90vh' },
        objectFit: 'contain' as const,
        display: 'block',
        cursor: onImageClick ? 'zoom-out' : 'default',
      }
    : {
        width: '100%',
        maxHeight,
        objectFit,
        display: 'block',
        cursor: onImageClick ? 'zoom-in' : 'default',
      };

  const activeColor = activeAnnotation
    ? colorFor(activeAnnotation.category)
    : CATEGORY_COLOR.object;
  const hasMoreDetails = !!(
    activeAnnotation?.more_details &&
    activeAnnotation.more_details.length > 0
  );

  return (
    <>
      <Box ref={containerRef} sx={containerSx}>
        <Box
          component="img"
          src={resolvedUrl}
          alt={alt || ''}
          onClick={handleImageClick}
          // Image load may change container height in lightbox mode.
          // Force a re-measurement so the first callout opened on a
          // freshly-loaded lightbox gets correct vertical clamping.
          onLoad={() => {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setContainerSize({ w: rect.width, h: rect.height });
          }}
          sx={imgSx}
        />

        {dotsVisible &&
          annotations.map((a) => {
            const isActive = a.id === activeId;
            const color = colorFor(a.category);
            return (
              <Box
                key={a.id}
                component="button"
                type="button"
                onClick={(e: React.MouseEvent) => handleDotClick(e, a.id)}
                aria-label={a.label}
                sx={{
                  position: 'absolute',
                  left: `${a.anchor.x * 100}%`,
                  top: `${a.anchor.y * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  width: isActive ? 22 : 16,
                  height: isActive ? 22 : 16,
                  borderRadius: '50%',
                  bgcolor: color,
                  border: '2px solid rgba(255,255,255,0.92)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  cursor: 'pointer',
                  padding: 0,
                  opacity: 0,
                  animation: 'annotated-dot-in 320ms ease-out forwards',
                  animationDelay: `${
                    Math.min(parseInt(a.id.split('_')[1] || '0', 10), 6) * 40
                  }ms`,
                  transition:
                    'width 160ms ease, height 160ms ease, transform 160ms ease',
                  '&::before': isActive
                    ? {
                        content: '""',
                        position: 'absolute',
                        inset: -6,
                        borderRadius: '50%',
                        border: `2px solid ${color}`,
                        opacity: 0.5,
                      }
                    : undefined,
                  '&:hover': {
                    width: 22,
                    height: 22,
                  },
                  '@keyframes annotated-dot-in': {
                    '0%': {
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(0.4)',
                    },
                    '60%': {
                      opacity: 1,
                      transform: 'translate(-50%, -50%) scale(1.15)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translate(-50%, -50%) scale(1)',
                    },
                  },
                }}
              />
            );
          })}

        {activeAnnotation && (
          <Box
            ref={calloutRef}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              left: calloutPosition
                ? `${calloutPosition.left}px`
                : `${CALLOUT_EDGE_PAD}px`,
              top: calloutPosition
                ? `${calloutPosition.top}px`
                : `${(activeAnnotation.anchor.y * (containerSize.h || 200)) +
                    CALLOUT_DOT_GAP}px`,
              maxWidth: CALLOUT_MAX_W,
              minWidth: CALLOUT_MIN_W,
              bgcolor: 'background.paper',
              color: 'text.primary',
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              border: `1px solid ${activeColor}33`,
              zIndex: 2,
              ...(containerSize.w > 0 &&
              containerSize.w < CALLOUT_MIN_W + CALLOUT_EDGE_PAD * 2
                ? {
                    maxWidth: containerSize.w - CALLOUT_EDGE_PAD * 2,
                    minWidth: 0,
                  }
                : {}),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: activeColor,
                  flex: 1,
                  lineHeight: 1.3,
                }}
              >
                {(activeAnnotation.confidence ?? 0.5) < 0.7 && (
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      color: 'text.secondary',
                      mr: 0.5,
                    }}
                  >
                    {t('Appears to be')}
                  </Box>
                )}
                {activeAnnotation.label}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveId(null);
                }}
                sx={{ p: 0.25, mt: -0.25, mr: -0.5 }}
                aria-label={t('Close')}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.82rem', lineHeight: 1.45 }}
            >
              {activeAnnotation.explanation}
            </Typography>
            {hasMoreDetails && (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  onClick={openDetails}
                  startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: activeColor,
                    px: 1,
                    py: 0.25,
                    minHeight: 0,
                    fontSize: '0.78rem',
                    '&:hover': {
                      bgcolor: `${activeColor}14`,
                    },
                  }}
                >
                  {t('More details')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <AnnotationDetailsModal
        annotation={detailsOpen ? activeAnnotation : null}
        accentColor={activeColor}
        onClose={closeDetails}
      />
    </>
  );
};
